
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { User, UserRoleType } from '@/types/user';
import { UserRole } from '@/types/roles';
import { fetchDefaultPrayerTimes } from '@/utils/defaultSettingsUtils';

interface UserDashboardData {
  user: User | null;
  userRole: UserRole | null;
  settings: Record<string, any>;
  times: any[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useUserDashboardData(): UserDashboardData {
  const { user: authUser } = useAuth();
  const [data, setData] = useState<UserDashboardData>({
    user: null,
    userRole: null,
    settings: {},
    times: [],
    isLoading: true,
    error: null,
    refetch: async () => fetchUserData()
  });

  const fetchUserData = async () => {
    if (!authUser?.id) {
      setData(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      console.log(`Fetching dashboard data for user ID: ${authUser.id}`);
      
      // Fetch user role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', authUser.id)
        .single();

      if (roleError) {
        console.error("Error fetching user role:", roleError);
        if (roleError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
          throw roleError;
        }
      }

      // Fetch user settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('user_settings')
        .select('settings_key, settings_value')
        .eq('user_id', authUser.id);

      if (settingsError) {
        console.error("Error fetching user settings:", settingsError);
        throw settingsError;
      }

      // Fetch user times with more detailed fields
      let timesData = [];
      const { data: userTimesData, error: timesError } = await supabase
        .from('times')
        .select('id, title, time, prayer_type, is_relative, relative_to, offset_minutes, show_time')
        .eq('user_id', authUser.id);

      if (timesError) {
        console.error("Error fetching times:", timesError);
        throw timesError;
      }

      // If no times found for this user, load default times
      if (!userTimesData || userTimesData.length === 0) {
        console.log("No times found for user, loading default times");
        const defaultTimes = await fetchDefaultPrayerTimes();
        if (defaultTimes && defaultTimes.length > 0) {
          timesData = defaultTimes;
          console.log("Using default times:", defaultTimes.length);
        }
      } else {
        timesData = userTimesData;
      }

      // Format settings into a usable object
      const formattedSettings: Record<string, any> = {};
      if (settingsData && settingsData.length > 0) {
        console.log(`Found ${settingsData.length} settings for user ${authUser.id}`);
        
        settingsData.forEach(setting => {
          formattedSettings[setting.settings_key] = setting.settings_value;
          console.log(`Loaded setting: ${setting.settings_key}`);
        });
      } else {
        console.log("No settings found for user");
      }

      // Debug log the fetched data
      console.log("User dashboard data fetched:", {
        role: roleData,
        settingsCount: settingsData?.length || 0,
        timesCount: timesData?.length || 0
      });

      // Construct user object with all data
      const userData: User = {
        id: authUser.id,
        email: authUser.email || '',
        role: roleData?.role as UserRoleType || 'viewer',
        is_active: roleData?.is_active !== undefined ? roleData.is_active : true,
        created_at: roleData?.created_at || new Date().toISOString(),
        organization_id: roleData?.team_id
      };

      setData({
        user: userData,
        userRole: roleData as UserRole,
        settings: formattedSettings,
        times: timesData || [],
        isLoading: false,
        error: null,
        refetch: fetchUserData
      });
    } catch (error) {
      console.error('Error fetching user dashboard data:', error);
      setData(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error instanceof Error ? error : new Error('Unknown error occurred'),
        refetch: fetchUserData
      }));
      
      toast({
        variant: "destructive",
        title: "Error loading user data",
        description: "There was an issue loading your profile. Please refresh or try again later."
      });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [authUser?.id]);

  return data;
}
